/**
 * Phone-number feature types (P4-T6 — design
 * `plans/designs/workspace-platform-p4-ui-design.md` §2/§12).
 *
 * Backend DTO mirrors of `services/voice-bridge/src/numbers/numbers.dto.ts` —
 * TypeScript interfaces, NOT classes (repo DTO convention; validation runs
 * server-side at the module boundary). `orgId` is NEVER request-supplyable
 * (design immutables): the server reads it from the verified credential, so no
 * form here accepts it.
 */

import type { AgentFactoryView } from '@/features/agents/types';

/** Policy actions enforced by `NumbersController` on `org:${orgId}` (design §2). */
export const NUMBERS_ACTIONS = {
  read: 'voice:numbers:read',
  create: 'voice:numbers:create',
  update: 'voice:numbers:update',
  delete: 'voice:numbers:delete',
  bind: 'voice:numbers:bind',
} as const;

/** `PhoneNumberView` mirror (`numbers.dto.ts:43-54`). */
export interface PhoneNumberView {
  id: string;
  /** E.164, stored verbatim — the UI never reformats it (design §6.2 F1). */
  number: string;
  orgId: string;
  projectId?: string;
  factoryId?: string;
  /** Webhook-integration identity ref — DISPLAY ONLY (design audit #10). */
  apiKeyId?: string;
  label?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PhoneNumberPagination {
  page: number;
  limit: number;
  total: number;
}

/** Page/limit envelope of `GET /v1/voice-bridge/numbers` (design §2). */
export interface NumberListResponse {
  data: PhoneNumberView[];
  pagination: PhoneNumberPagination;
}

export interface ListNumbersArgs {
  page?: number;
  /** Server default 20, max 100 — the client caps here too (design §6.4). */
  limit?: number;
  projectId?: string;
  /** `'true' | 'false'` — omit for all (`ListNumbersQuery`). */
  isActive?: 'true' | 'false';
}

/** `POST /v1/voice-bridge/numbers` body mirror. */
export interface CreatePhoneNumberDto {
  number: string;
  label?: string;
  projectId?: string;
}

/**
 * `PATCH /v1/voice-bridge/numbers/:id` body mirror. `factoryId` is NOT
 * patchable (bind endpoint only — API fact, design §8). `projectId: null`
 * clears the association (design §8/OQ-8 — the server-side semantics are the
 * backend's open question, the UI contract is explicit).
 */
export interface UpdatePhoneNumberDto {
  label?: string;
  projectId?: string | null;
  isActive?: boolean;
}

export interface UpdateNumberArgs {
  id: string;
  changes: UpdatePhoneNumberDto;
}

export interface BindNumberArgs {
  id: string;
  factoryId: string;
}

/** `GET /voice/voices` row, trimmed to the forwarding step's needs (design §2). */
export interface VoiceView {
  id: string;
  name?: string;
  description?: string;
  language?: string;
  providerVoiceId?: string;
  ttsModel?: string;
  projectId?: string;
  isActive?: boolean;
}

/** Mirror of `TemplateForwardingCfg` (`agent-core` agent-template.schema.ts:97-114). */
export interface TemplateForwardingCfg {
  enabled?: boolean;
  audioOut?: { enabled?: boolean; voiceId?: string };
  bindNumberRequired?: boolean;
  greeting?: string;
}

/**
 * Draft value written by the `forwarding` step (design §12.1 — mirrors
 * `TemplateForwardingCfg`). The P2 frame persists step values in
 * `draft.steps[key]` (the named-slice map only covers attach-* types); the
 * create-payload merge (`overrides.forwarding`, OQ-3) consumes it P2-side.
 */
export interface ForwardingStepValue {
  enabled: boolean;
  audioOut: { enabled: boolean; voiceId?: string };
  greeting: string;
}

/**
 * Draft value written by the `bind-number` step (design §12.2) — an INTENT
 * executed post-create by the P2 §15.3 attach sequence.
 */
export interface BindNumberStepValue {
  numberId: string;
}

/**
 * `GET /agent/factories` rows carry `forwarding` on the server view
 * (`factories.service.ts` — design §2) even though the P2 frontend
 * `AgentFactoryView` predates it; this is the cast target for the voice
 * grouping (audit #5 — computed key, never a name/kind hardcode).
 */
export interface FactoryWithForwarding extends AgentFactoryView {
  forwarding?: TemplateForwardingCfg | null;
}

export function isForwardingStepValue(value: unknown): value is ForwardingStepValue {
  return Boolean(
    value &&
    typeof value === 'object' &&
    typeof (value as ForwardingStepValue).enabled === 'boolean' &&
    (value as ForwardingStepValue).audioOut &&
    typeof (value as ForwardingStepValue).audioOut === 'object',
  );
}

export function isBindNumberStepValue(value: unknown): value is BindNumberStepValue {
  return Boolean(
    value &&
    typeof value === 'object' &&
    typeof (value as BindNumberStepValue).numberId === 'string' &&
    (value as BindNumberStepValue).numberId !== '',
  );
}
